var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { $auth } from 'ziphy-web-shared/basic/entities/auth';
import { personAgeLimit } from 'ziphy-web-shared/basic/helpers';
import { useInnerValue } from 'ziphy-web-shared/basic/hooks';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { useForm } from 'ziphy-web-shared/basic/lib/forms';
import { DateInput, LegacyInput, PhoneInput } from 'ziphy-web-shared/basic/lib/forms/fields';
import { TextWithDash } from 'ziphy-web-shared/basic/lib/simple';
import { RadioTabs } from 'ziphy-web-shared/basic/lib/tabs';
import { $awsCaptcha, AwsCaptcha, CodeResenderCallMeButton, CodeResenderInput, CodeResenderResendButton, showAlert, useCodeResender, } from 'ziphy-web-shared/basic/lib/utilities';
import { $loader } from 'ziphy-web-shared/basic/utils';
import ChangeLanguage from '@nedoShared/features/i18n/changeLanguage/ChangeLanguage';
import mainConfig from '@config/main';
import { useLoginForm } from './LoginForm.hooks';
import styles from './LoginForm.module.scss';
const LOGIN_LOADER = 'auth-login-submit';
const LoginForm = ({ view: _view = 'default', onChangeView, accountType: _accountType = 'phone', onChangeAccountType, redirectAfterComplete, waitAllConfigureSteps, 
//
hasOnboarding = false, invitationCode, 
//
hasLanguageSwitcher = true, _core, }) => {
    const { t } = useTranslation();
    const { handleOnComplete } = useLoginForm({ redirectAfterComplete, waitAllConfigureSteps, _core });
    const [view, setView] = useInnerValue(_view);
    useEffect(() => onChangeView === null || onChangeView === void 0 ? void 0 : onChangeView(view), [view]);
    const [accountType, setAccountType] = useInnerValue(_accountType);
    useEffect(() => onChangeAccountType === null || onChangeAccountType === void 0 ? void 0 : onChangeAccountType(accountType), [accountType]);
    const formPhone = useForm({
        account: {
            value: '',
            rules: [{ name: 'phone', required: true }],
            output: ['phone'],
        },
    });
    const formEmail = useForm({
        account: { value: '', rules: ['email', 'required'] },
    });
    const { form: accountForm } = accountType === 'phone' ? formPhone : formEmail;
    const { account } = accountForm.fields;
    const dobType = 'profile';
    const { form: commonForm } = useForm({
        patientDob: { value: '', rules: [{ name: 'dob', dobType }, 'required'], output: 'date' },
    });
    const { form: withCodeForm } = useForm({
        onboardCode: { value: invitationCode || '', rules: ['required'] },
    });
    const { form: withoutCodeForm } = useForm({
        firstName: { value: '', rules: ['name', 'required'] },
        lastName: { value: '', rules: ['name', 'required'] },
        patientDos: { value: '', rules: ['required'], output: 'date' },
    });
    const codeResender = useCodeResender({
        onSubmit: useCallback((messageType) => __awaiter(void 0, void 0, void 0, function* () {
            if (view === 'with_code') {
                return yield $auth.sendCode(Object.assign(Object.assign({ type: accountType, using: messageType, value: account.getOutputValue() }, commonForm.values), withCodeForm.values));
            }
            else if (view === 'without_code') {
                return yield $auth.sendCode(Object.assign(Object.assign({ type: accountType, using: messageType, value: account.getOutputValue() }, commonForm.values), withoutCodeForm.values));
            }
            return yield $auth.sendCode({
                type: accountType,
                using: messageType,
                value: account.getOutputValue(),
            });
        }), [view, accountType]),
        clearDeps: [
            view,
            accountType,
            accountForm.values,
            commonForm.values,
            withCodeForm.values,
            withoutCodeForm.values,
        ],
    });
    const clearAll = useCallback(() => {
        account.onChange('');
        codeResender.clear();
    }, [view, accountType]);
    const validateAll = useCallback(() => {
        const account = accountForm.validateAll().isValid;
        let common = true;
        let additional = true;
        if (view === 'with_code') {
            common = commonForm.validateAll().isValid;
            additional = withCodeForm.validateAll().isValid;
        }
        else if (view === 'without_code') {
            common = commonForm.validateAll().isValid;
            additional = withoutCodeForm.validateAll().isValid;
        }
        return account && common && additional;
    }, [view, accountType]);
    const handleSubmitCode = $loader.registerHandler(LOGIN_LOADER, (e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        e.preventDefault();
        if (!validateAll())
            return;
        _core === null || _core === void 0 ? void 0 : _core.onBlock();
        const response = yield handleSendTextCode();
        if (!$awsCaptcha.enabled) {
            if (((_b = (_a = response.error) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.reason) === 'Incorrect phone number') {
                accountForm.forceError('account');
            }
        }
        _core === null || _core === void 0 ? void 0 : _core.onUnblock();
    }));
    const handleSubmitDone = $loader.registerHandler(LOGIN_LOADER, (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (codeResender.form.validateAll().isInvalid || !validateAll())
            return;
        _core === null || _core === void 0 ? void 0 : _core.onBlock();
        const response = yield $auth.loginByCode({
            type: accountType,
            value: account.getOutputValue(),
            code: codeResender.code.getOutputValue(),
        });
        if (response.prepared) {
            handleOnComplete();
            clearAll();
        }
        else if (response.error) {
            if (response.error.code === 'account_role') {
                showAlert.error(response.error.message, { isApiMessage: true });
                clearAll();
            }
            else {
                codeResender.form.forceError('code');
            }
        }
        _core === null || _core === void 0 ? void 0 : _core.onUnblock();
    }));
    const handleSendTextCode = useCallback(() => __awaiter(void 0, void 0, void 0, function* () { return yield codeResender.sendCode('text'); }), [codeResender.sendCode]);
    return (_jsxs("form", Object.assign({ id: "login_form", onSubmit: codeResender.isWaiting ? handleSubmitDone : handleSubmitCode, noValidate: true }, { children: [view === 'default' ? (_jsx(_Fragment, { children: accountType === 'phone' ? (_jsx(PhoneInput, { mode: 'md', field: account })) : (_jsx(LegacyInput, { field: account, type: "email", mode: "noLabel", placeholder: t('label.email') }, 'email')) })) : (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: 'mb-10' }, { children: [_jsx(RadioTabs, { value: view, setValue: setView, items: [
                                    { id: 'with_code', text: t('label.with_code') },
                                    { id: 'without_code', text: t('label.without_code') },
                                ], className: classNames(styles.tabs, 'mb-10'), mode: "modern" }), view === 'with_code' ? (_jsx(_Fragment, { children: _jsx(LegacyInput, { field: withCodeForm.fields.onboardCode, mode: "lg", label: t('label.invitation_code'), className: 'mb-10', readonly: Boolean(invitationCode), maxLength: 10 }, 'onboard_code') })) : (view === 'without_code' && (_jsxs(_Fragment, { children: [_jsx(LegacyInput, { field: withoutCodeForm.fields.firstName, mode: "lg", label: t('label.first_name'), className: 'mb-10' }), _jsx(LegacyInput, { field: withoutCodeForm.fields.lastName, mode: "lg", label: t('label.last_name'), className: 'mb-10' })] }))), _jsx(DateInput, { field: commonForm.fields.patientDob, mode: "lg", label: t('label.date_of_birth'), className: 'mb-10', minDate: personAgeLimit(dobType).from, maxDate: personAgeLimit(dobType).to }, 'dob'), view === 'without_code' && (_jsx(DateInput, { field: withoutCodeForm.fields.patientDos, mode: "lg", label: t('label.date_of_service'), className: 'mb-10', maxDate: moment().endOf('day') }))] })), accountType === 'phone' ? (_jsx(PhoneInput, { field: account, mode: "lg", label: t('label.phone') })) : (_jsx(LegacyInput, { field: account, type: "email", mode: "lg", label: t('label.email') }, 'email'))] })), _jsx(AwsCaptcha, { show: $awsCaptcha.enabled, apiKey: mainConfig.awsCaptcha.key, domainList: mainConfig.awsCaptcha.domains }), codeResender.isWaiting && (_jsx(CodeResenderInput, { code: codeResender.code, messageType: codeResender.messageType, className: 'mt-10' })), _jsx("div", Object.assign({ className: "mt-10" }, { children: _jsx(Button, Object.assign({ mode: ['primary', 'block'], form: "login_form", isLoading: $loader.isRunHandler(LOGIN_LOADER) }, { children: codeResender.isWaiting ? t('btn.continue') : t(`label.continue_with_${accountType}`) })) })), !codeResender.isWaiting && (_jsxs(_Fragment, { children: [_jsx(TextWithDash, Object.assign({ className: 'mt-15 mb-15' }, { children: t('label.or') })), _jsx(Button, Object.assign({ mode: ['secondary', 'block'], onClick: () => setAccountType((x) => (x === 'phone' ? 'email' : 'phone')) }, { children: t(`label.continue_with_${accountType === 'phone' ? 'email' : 'phone'}`) })), hasOnboarding && view === 'default' && (_jsxs("div", Object.assign({ className: classNames(styles.additionalText, 'mt-20') }, { children: [t('label.have_an_invitation_code?'), ' ', _jsx(Button, Object.assign({ mode: ['anchor-gray-underline'], onClick: () => setView('with_code') }, { children: t('btn.enter_it_here') }))] })))] })), codeResender.isWaiting && (_jsx(CodeResenderResendButton, { initTime: codeResender.codeSent, onResend: handleSendTextCode, className: 'mt-10' })), codeResender.isWaiting && accountType === 'phone' && (_jsx(CodeResenderCallMeButton, { onClick: codeResender.callMe, className: 'mt-20' })), hasLanguageSwitcher && (_jsx(ChangeLanguage, { children: _jsx("div", Object.assign({ className: classNames(styles.changeLanguage, styles.a, 'mt-10') }, { children: t('label.change_language') })) }))] })));
};
export default observer(LoginForm);
